import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DoneIcon from '@mui/icons-material/Done';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import { Paper } from '@src/common/components/Paper';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { typography } from '@src/common/styles/valueTokens';
import { UpdatableValue } from '@src/orders/components/UpdatableValue';
import { type ComponentProps, type ElementType, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type TypographyProps = Pick<
  ComponentProps<typeof Typography>,
  'variant' | 'id' | 'style' | 'className'
> & { component?: ElementType; color?: string };

type CopyableTypographyProps = {
  textBefore?: string;
  textAfter?: string;
  paddingY?: string;
  paddingX?: string;
  lastUpdated?: string;
  children?: string;
} & Omit<TypographyProps, 'children'>;

export const CopyableTypography = ({
  children,
  textBefore,
  textAfter,
  paddingY,
  paddingX,
  className,
  lastUpdated,
  ...props
}: CopyableTypographyProps) => {
  const { t } = useTranslation();
  const [showCopied, setShowCopied] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const ref = useRef(null);
  const value = textAfter ? children?.concat(' ', textAfter) : children;
  const copyToClipboard = async () => {
    if (!children || children.length === 0) {
      return;
    }
    await navigator.clipboard.writeText(children);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 1000);
  };

  const iconStyle = {
    marginLeft: theme.spacing['2'],
    marginRight: paddingX,
  };

  return (
    <>
      <Popper
        id={children}
        open={showCopied}
        anchorEl={ref.current}
        placement="top-end"
      >
        <Paper className={paper}>
          <Typography style={{ ...typography.body.small }} color="#F1F0F7">
            {t('copied')}
          </Typography>
        </Paper>
      </Popper>

      <Stack direction="row">
        {textBefore && (
          <Typography
            {...props}
            style={{
              paddingLeft: paddingX,
              paddingTop: paddingY,
              paddingBottom: paddingY,
            }}
          >
            {textBefore}
          </Typography>
        )}

        <Stack
          direction="row"
          className={copyableText}
          onClick={copyToClipboard}
          alignItems="center"
          paddingLeft={paddingX}
          paddingY={paddingY}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <UpdatableValue
            {...props}
            className={className}
            style={{ height: '100%' }}
            lastUpdated={lastUpdated}
          >
            {value}
          </UpdatableValue>

          {isHovering && !showCopied && (
            <Tooltip title={t('copy')} placement="top">
              <ContentCopyIcon
                style={{
                  ...iconStyle,
                  color: '#444650',
                  paddingTop: '2px',
                  paddingBottom: '2px',
                }}
              />
            </Tooltip>
          )}

          <DoneIcon
            ref={ref}
            style={{
              ...iconStyle,
              color: 'green',
              display: showCopied ? '' : 'none',
            }}
          />
        </Stack>
      </Stack>
    </>
  );
};

const paper = css({
  padding: `${theme.spacing['1']} ${theme.spacing['2']}`,
  backgroundColor: '#2F3036',
});

const copyableText = css({
  cursor: 'default',

  '&:hover': {
    backgroundColor: '#EDEDED',
    borderRadius: '2px',
  },
});
